import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Film, GraduationCap, Radio } from 'lucide-react';
import {
  useCourses,
  useBacklog,
  useNextLive,
  useUpdateLessonStatus,
  type LessonStatus,
  type CourseFormato,
} from '@/hooks/useCourses';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const STATUS_ORDER: LessonStatus[] = [
  'planejada',
  'roteirizada',
  'gravada',
  'editada',
  'publicada',
];

const STATUS_LABEL: Record<LessonStatus, string> = {
  planejada: 'Planejada',
  roteirizada: 'Roteirizada',
  gravada: 'Gravada',
  editada: 'Editada',
  publicada: 'Publicada',
};

const STATUS_COLOR: Record<LessonStatus, string> = {
  planejada: 'bg-zinc-500/20 text-zinc-400',
  roteirizada: 'bg-blue-500/20 text-blue-400',
  gravada: 'bg-purple-500/20 text-purple-400',
  editada: 'bg-amber-500/20 text-amber-400',
  publicada: 'bg-emerald-500/20 text-emerald-500',
};

const FORMATO_LABEL: Record<CourseFormato, string> = {
  'curso-online': 'Curso online',
  formacao: 'Formação',
  'mentoria-grupo': 'Mentoria em grupo',
  'imersao-presencial': 'Imersão presencial',
  'aula-avulsa': 'Aula avulsa',
};

export function CursosPage(): JSX.Element {
  const [formato, setFormato] = useState<CourseFormato | ''>('');
  const { data: coursesData, isLoading } = useCourses({ formato: formato || undefined });
  const { data: nextLive } = useNextLive();
  const { data: backlogData } = useBacklog();

  const cursosEstruturados = (coursesData?.courses ?? []).filter(
    (c) => c.metadata?.formato !== 'aula-avulsa',
  );
  const aulasAvulsas = (coursesData?.courses ?? []).filter(
    (c) => c.metadata?.formato === 'aula-avulsa',
  );

  const nextLiveList = nextLive?.next ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Cursos & Formações</h1>
        <p className="text-sm text-muted-foreground">
          Hub de descoberta — cursos, formações, aulas avulsas e backlog de produção.
        </p>
      </div>

      {/* Card próximas aulas */}
      {nextLiveList.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Radio className="h-4 w-4 text-emerald-500" /> Próximas aulas ao vivo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1.5">
              {nextLiveList.slice(0, 5).map((l, i) => (
                <li key={`${l.course_item_id}-${i}`} className="flex items-center gap-2 text-sm">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-mono text-xs text-muted-foreground w-28">
                    {new Date(l.ao_vivo_em).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  <Link
                    to={`/items/${l.course_item_id}`}
                    className="hover:text-primary truncate flex-1"
                  >
                    {l.lesson_nome
                      ? `${l.lesson_nome} — ${l.course_nome}`
                      : l.course_nome}
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-[1fr_280px] gap-4">
        {/* Main: tabs de cursos */}
        <div>
          <Tabs defaultValue="cursos">
            <TabsList>
              <TabsTrigger value="cursos" className="gap-1">
                <GraduationCap className="h-3.5 w-3.5" />
                Cursos & Formações ({cursosEstruturados.length})
              </TabsTrigger>
              <TabsTrigger value="avulsas" className="gap-1">
                <Film className="h-3.5 w-3.5" />
                Aulas Avulsas ({aulasAvulsas.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="cursos">
              <div className="flex gap-2 items-center mb-3">
                <select
                  value={formato}
                  onChange={(e) => setFormato(e.target.value as CourseFormato | '')}
                  className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                >
                  <option value="">Todos os formatos</option>
                  {(Object.entries(FORMATO_LABEL) as Array<[CourseFormato, string]>)
                    .filter(([k]) => k !== 'aula-avulsa')
                    .map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                </select>
                {formato && (
                  <button
                    type="button"
                    onClick={() => setFormato('')}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Limpar
                  </button>
                )}
              </div>

              <CourseList
                courses={cursosEstruturados}
                loading={isLoading}
                emptyLabel="Nenhum curso cadastrado. Adicione um com tipo 'Curso' em '+ Adicionar'."
              />
            </TabsContent>

            <TabsContent value="avulsas">
              <CourseList
                courses={aulasAvulsas}
                loading={isLoading}
                emptyLabel="Nenhuma aula avulsa cadastrada."
              />
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar: backlog */}
        <aside>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1.5">
                🎬 Backlog de Produção
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!backlogData || backlogData.backlog.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhuma aula em backlog 🎉</p>
              ) : (
                <ul className="space-y-2">
                  {STATUS_ORDER.filter((s) => s !== 'publicada').map((status) => {
                    const entry = backlogData.backlog.find((b) => b.status === status);
                    if (!entry) return null;
                    return (
                      <li key={status}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium">{STATUS_LABEL[status]}</span>
                          <Badge className={cn('text-[10px]', STATUS_COLOR[status])}>
                            {entry.count}
                          </Badge>
                        </div>
                        <ul className="space-y-0.5 ml-2">
                          {entry.aulas.slice(0, 4).map((a) => (
                            <li key={a.item_id}>
                              <Link
                                to={`/items/${a.item_id}`}
                                className="text-[11px] text-muted-foreground hover:text-foreground line-clamp-1"
                              >
                                {a.nome}
                                {a.course_nome && (
                                  <span className="text-muted-foreground/70"> — {a.course_nome}</span>
                                )}
                              </Link>
                            </li>
                          ))}
                          {entry.aulas.length > 4 && (
                            <li className="text-[10px] text-muted-foreground/70">
                              +{entry.aulas.length - 4}…
                            </li>
                          )}
                        </ul>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function CourseList({
  courses,
  loading,
  emptyLabel,
}: {
  courses: Array<{
    item: { id: number; nome: string; status: string; url: string | null; descricao: string | null };
    metadata: { formato: CourseFormato | null; numero_aulas?: number; data_lancamento?: string | null } | null;
    numero_aulas: number;
  }>;
  loading: boolean;
  emptyLabel: string;
}): JSX.Element {
  if (loading) return <p className="text-sm text-muted-foreground">Carregando…</p>;
  if (courses.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">{emptyLabel}</p>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {courses.map((c) => (
        <Link key={c.item.id} to={`/items/${c.item.id}`}>
          <Card className="h-full hover:border-primary/40 transition-colors">
            <CardHeader className="pb-2">
              <CardTitle className="text-base truncate">{c.item.nome}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {c.item.descricao && (
                <p className="text-xs text-muted-foreground line-clamp-2">{c.item.descricao}</p>
              )}
              <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
                {c.metadata?.formato && (
                  <Badge variant="outline">{FORMATO_LABEL[c.metadata.formato]}</Badge>
                )}
                <Badge variant="secondary">{c.item.status}</Badge>
                <span className="text-muted-foreground">· {c.numero_aulas} aulas</span>
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}
